import React, { useEffect, useState } from 'react';

import { jwtDecode } from 'jwt-decode';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { LoginForm } from '~/stormpath';
import UserActions from '~/stormpath/actions/UserActions';

import { Alert, Box, Button, Container, TextField, Typography } from '@mui/material';

import { GoogleTokenData } from '~/types/google';

import ExpiredTokenMessage from './Auth/Messages/ExpiredTokenMessage';
import SocialGoogleButton from './Auth/SocialLogin/SocialGoogleButton';

function LoginPage() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showExpiredTokenMessage, setShowExpiredTokenMessage] = useState(false);
  const [isProcessingCredential, setIsProcessingCredential] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const onSubmitError = ({ error }, callback) => {
    setErrorMessage(error.message);
    callback();
  };

  useEffect(() => {
    setShowExpiredTokenMessage(localStorage.getItem('showExpiredTokenMessage') === 'true');
    return () => {
      localStorage.removeItem('showExpiredTokenMessage');
    };
  }, []);

  // Handle credential from URL for redirect flows (review/canary apps using shared auth domain).
  // When redirected back from the shared auth domain, the URL contains ?credential=JWT.
  useEffect(() => {
    const windowParams = new URLSearchParams(window.location.search);
    const credential = windowParams.get('credential');
    if (!credential) {
      return;
    }

    // Remove credential from URL FIRST to prevent JWT leaking via Referer, browser history,
    // or bookmarks. This must happen before any early returns or login dispatch.
    // Admin does not use connected-react-router, so window.history.replaceState is safe
    // (no Redux router state to desync).
    windowParams.delete('credential');
    const cleanSearch = windowParams.toString();
    window.history.replaceState({}, '', window.location.pathname + (cleanSearch ? `?${cleanSearch}` : ''));

    // Gate credential processing behind the same feature flag that controls the Google button.
    // Without this, a user could bypass disabled Google login by manually hitting the shared
    // auth endpoint and returning with a valid ?credential= param. (P2 – feature flag bypass)
    if (window.configs.GOOGLE_LOGIN_ENABLED !== 'true') {
      setErrorMessage('Google login is currently disabled');
      return;
    }

    setIsProcessingCredential(true);

    try {
      const tokenData = jwtDecode<GoogleTokenData>(credential);
      const loginWithGoogleData = {
        id_google: tokenData.sub,
        email: tokenData.email,
        token_id: credential,
        givenName: tokenData.given_name,
        surname: tokenData.family_name,
      };
      UserActions.loginWithGoogle(loginWithGoogleData, (err) => {
        if (err) {
          setIsProcessingCredential(false);
          setErrorMessage(err.message || 'Google login failed');
          return;
        }
        history.push('/');
      });
    } catch (err) {
      setIsProcessingCredential(false);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to process Google credential');
    }
  }, [history]);

  const recaptchaKey = window.configs.RECAPTCHA_KEY;

  // Show a loading state while processing credential from shared auth domain redirect
  if (isProcessingCredential) {
    return (
      <Container>
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography>Signing in with Google...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      {showExpiredTokenMessage && <ExpiredTokenMessage />}
      <Helmet>
        <title>Login</title>
      </Helmet>

      <Box className="row flex-centered">
        <Box sx={{ mt: 6 }}>
          <h1 className="page-header">Login</h1>
          <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey} useEnterprise>
            {window.configs.GOOGLE_LOGIN_ENABLED === 'true' &&
              (window.google || window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN) && (
                <>
                  <SocialGoogleButton />
                  <Typography component="p" sx={{ my: 2 }}>
                    {' '}
                    - or -
                  </Typography>
                </>
              )}

            <LoginForm onSubmitError={onSubmitError} redirectTo={`${location.pathname}${location.search}`}>
              <Box className="sp-login-form">
                <TextField
                  id="sp-login-0"
                  name="login"
                  type="text"
                  placeholder="adam@luxuryescapes.com"
                  label="Username or email"
                  fullWidth
                  required
                />

                <TextField
                  id="sp-password-1"
                  name="password"
                  type="password"
                  placeholder="SuperSecret"
                  label="Password"
                  fullWidth
                  required
                />

                <Box>
                  <Alert severity="warning" data-spif="form.error" sx={{ my: 2 }}>
                    <Typography component="span">{errorMessage || 'Username or password was incorrect'}</Typography>
                  </Alert>

                  <Button variant="contained" size="large" type="submit" fullWidth>
                    Login with credentials
                  </Button>
                </Box>
              </Box>
            </LoginForm>
          </GoogleReCaptchaProvider>
        </Box>
      </Box>
    </Container>
  );
}

export default LoginPage;
